import "../../pages/reports/gce-o.css";
import { ExamType } from "../../types/types";

interface SubjectData {
  code: string;
  name: string;
  student_count: string | number;
}

interface CategorizedSubject {
  code: string;
  name: string;
  counts: {
    a4?: number;
    bil?: number;
    c?: number;
    d?: number;
    ti?: number;
  };
}

interface Row {
  subject: string;
  a4: string | number;
  bil: string | number;
  c: string | number;
  d: string | number;
  ti: string | number;
}

interface BACCSubjectsTableProps {
  registrationData: {
    exam_registration?: {
      subject_categories?: Record<string, SubjectData[]>;
    } | null;
  } | null;
  selectedExamTypes: ExamType[];
}

const BACCSubjectsTable: React.FC<BACCSubjectsTableProps> = ({
  registrationData,
  selectedExamTypes,
}) => {
  const shouldRenderTables = selectedExamTypes.some((et) =>
    et.name.includes("BACC")
  ); // Use .some() instead of finding the exact one and checking

  if (!shouldRenderTables) return null;

  const baccA4 =
    registrationData?.exam_registration?.subject_categories?.BACCA4 || [];
  const baccBil =
    registrationData?.exam_registration?.subject_categories?.BACCBIL || [];
  const baccC =
    registrationData?.exam_registration?.subject_categories?.BACCC || [];
  const baccD =
    registrationData?.exam_registration?.subject_categories?.BACCD || [];
  const baccTI =
    registrationData?.exam_registration?.subject_categories?.BACCTI || [];

  const subjectMap = new Map<
    string,
    {
      name: string;
      counts: {
        a4?: number;
        bil?: number;
        c?: number;
        d?: number;
        ti?: number;
      };
    }
  >();

  baccA4.forEach((s) => {
    if (!subjectMap.has(s.code))
      subjectMap.set(s.code, { name: s.name, counts: {} });
    subjectMap.get(s.code)!.counts.a4 = Number(s.student_count);
  });
  baccBil.forEach((s) => {
    if (!subjectMap.has(s.code))
      subjectMap.set(s.code, { name: s.name, counts: {} });
    subjectMap.get(s.code)!.counts.bil = Number(s.student_count);
  });
  baccC.forEach((s) => {
    if (!subjectMap.has(s.code))
      subjectMap.set(s.code, { name: s.name, counts: {} });
    subjectMap.get(s.code)!.counts.c = Number(s.student_count);
  });
  baccD.forEach((s) => {
    if (!subjectMap.has(s.code))
      subjectMap.set(s.code, { name: s.name, counts: {} });
    subjectMap.get(s.code)!.counts.d = Number(s.student_count);
  });
  baccTI.forEach((s) => {
    if (!subjectMap.has(s.code))
      subjectMap.set(s.code, { name: s.name, counts: {} });
    subjectMap.get(s.code)!.counts.ti = Number(s.student_count);
  });

  const allSubjects = Array.from(subjectMap.entries()).map(([code, data]) => ({
    code,
    name: data.name,
    counts: data.counts,
  }));

  const allMerged: CategorizedSubject[] = [];
  const cdtiMerged: CategorizedSubject[] = [];
  const a4bilMerged: CategorizedSubject[] = [];
  const cdMerged: CategorizedSubject[] = [];
  const a4Only: CategorizedSubject[] = [];
  const bilOnly: CategorizedSubject[] = [];
  const cOnly: CategorizedSubject[] = [];
  const dOnly: CategorizedSubject[] = [];
  const tiOnly: CategorizedSubject[] = [];

  for (const subj of allSubjects) {
    const { counts } = subj;
    const hasA4 = counts.a4 !== undefined;
    const hasBil = counts.bil !== undefined;
    const hasC = counts.c !== undefined;
    const hasD = counts.d !== undefined;
    const hasTi = counts.ti !== undefined;

    if (hasA4 && hasBil && hasC && hasD && hasTi) {
      allMerged.push(subj);
    } else if (hasC && hasD && hasTi && !hasA4 && !hasBil) {
      cdtiMerged.push(subj);
    } else if (hasA4 && hasBil && !hasC && !hasD && !hasTi) {
      a4bilMerged.push(subj);
    } else if (hasC && hasD && !hasA4 && !hasBil && !hasTi) {
      cdMerged.push(subj);
    } else {
      if (hasA4 && !hasBil && !hasC && !hasD && !hasTi) a4Only.push(subj);
      else if (hasBil && !hasA4 && !hasC && !hasD && !hasTi) bilOnly.push(subj);
      else if (hasC && !hasA4 && !hasBil && !hasD && !hasTi) cOnly.push(subj);
      else if (hasD && !hasA4 && !hasBil && !hasC && !hasTi) dOnly.push(subj);
      else if (hasTi && !hasA4 && !hasBil && !hasC && !hasD) tiOnly.push(subj);
    }
  }

  const rows: Row[] = [
    ...allMerged.map((s) => ({
      subject: `${s.code} ${s.name}`,
      a4: s.counts.a4!,
      bil: s.counts.bil!,
      c: s.counts.c!,
      d: s.counts.d!,
      ti: s.counts.ti!,
    })),
    ...cdtiMerged.map((s) => ({
      subject: `${s.code} ${s.name}`,
      a4: "N/A",
      bil: "N/A",
      c: s.counts.c!,
      d: s.counts.d!,
      ti: s.counts.ti!,
    })),
    ...a4bilMerged.map((s) => ({
      subject: `${s.code} ${s.name}`,
      a4: s.counts.a4!,
      bil: s.counts.bil!,
      c: "N/A",
      d: "N/A",
      ti: "N/A",
    })),
    ...cdMerged.map((s) => ({
      subject: `${s.code} ${s.name}`,
      a4: "N/A",
      bil: "N/A",
      c: s.counts.c!,
      d: s.counts.d!,
      ti: "N/A",
    })),
    ...a4Only.map((s) => ({
      subject: `${s.code} ${s.name}`,
      a4: s.counts.a4!,
      bil: "N/A",
      c: "N/A",
      d: "N/A",
      ti: "N/A",
    })),
    ...bilOnly.map((s) => ({
      subject: `${s.code} ${s.name}`,
      a4: "N/A",
      bil: s.counts.bil!,
      c: "N/A",
      d: "N/A",
      ti: "N/A",
    })),
    ...cOnly.map((s) => ({
      subject: `${s.code} ${s.name}`,
      a4: "N/A",
      bil: "N/A",
      c: s.counts.c!,
      d: "N/A",
      ti: "N/A",
    })),
    ...dOnly.map((s) => ({
      subject: `${s.code} ${s.name}`,
      a4: "N/A",
      bil: "N/A",
      c: "N/A",
      d: s.counts.d!,
      ti: "N/A",
    })),
    ...tiOnly.map((s) => ({
      subject: `${s.code} ${s.name}`,
      a4: "N/A",
      bil: "N/A",
      c: "N/A",
      d: "N/A",
      ti: s.counts.ti!,
    })),
  ];

  return (
    <table className="gceo-subjects-table">
      <tbody>
        <tr>
          <th colSpan={2}>MATIERES / SERIES</th>
          <th># of Candidates A4</th>
          <th># of Candidates BIL</th>
          <th># of Candidates C</th>
          <th># of Candidates D</th>
          <th># of Candidates TI</th>
        </tr>
        {rows.map((row, index) => (
          <tr key={index} style={{ fontSize: "8px", lineHeight: "5px" }}>
            <td colSpan={2}>{row.subject}</td>
            <td>{row.a4}</td>
            <td>{row.bil}</td>
            <td>{row.c}</td>
            <td>{row.d}</td>
            <td>{row.ti}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default BACCSubjectsTable;
